import api from './api';
import { getUserRole, getUserBadge } from '@/utils/navigation';

export interface SkillPathway {
  id: string;
  name: string;
  description: string;
  category: string;
  core_category_id?: string|number;
  core_category_name: string;
  core_category_slug?: string;
  progress: number;
  isSelected?: boolean;
  enrolled?: boolean;
  is_enrolled: boolean;
  learning_progress: number;
  versions: any[];
  skillstree_id: number;
  image_url?: string;
  duration: number;
  slug: string;
  badges?: any;
}

export interface ProgressData {  
  total_skillstrees: number,
  completed_skillstrees: number,
  total_micro_skills_started: number,
  total_micro_skills_completed: number,
  total_badges: number,
  total_passports: number,
  total_events_registered: number,
  total_events_attended: number
  microSkills: number;
  microLearning: number;
  achievements: number;
  overall: number;
  completedModules: number;
  totalModules: number;
  currentPathway?: string;
}

export interface UserProfile {
  id: number;
  first_name?: string;
  last_name?: string;
  email: string;
  country: string;
  gender: string;
  name?: string;
  avatar?: string;
  role?: any;
}

export interface DashboardData {
  user: UserProfile;
  skillPathways: SkillPathway[];
  groupedPathways: { [category: string]: SkillPathway[] }; // New grouped data
  selectedPathway?: SkillPathway | null;
  progress: ProgressData;
  profilePathways?: SkillPathway[];
  enrolledPathways: number;
  maxPathwaySlots: number;
  pathwayCount: number;
  achievementCount: number;
  coachingCount: number;
  networkingCount: number;
  upcomingSessions: any[];
  networkingEvents: any[];
  planValidUntil: any;
  isPlus: boolean;
  statistics?: {
      total_skillstrees: number,
      completed_skillstrees: number,
      total_micro_skills_started: number,
      total_micro_skills_completed: number,
      total_badges: number,
      total_passports: number,
      total_events_registered: number,
      total_events_attended: number
      remaining_coaching: number,
      remaining_networking: number,
      attended_coaching: number,
      attended_networking: number,
    };
}

export interface MicroSkill {
  micro_skill_version_id: string,
  micro_skill_id: string,
  title: string,
  version: string,
  unique_code: string,
  description: string,
  image_url: string,
  duration: string,
  is_free: boolean,
  can_access: boolean,
  learning_status: string,
  learning_progress: string|number,
  videos: any[]
}

export const dashboardService = {
  async getDashboardData(): Promise<DashboardData> {
    try {
      // Get skillstrees and profile data (required)
      const [skillstreesResponse, profileResponse] = await Promise.all([
        this.getSkillstrees(),
        this.getUserProfile()
      ]);

      const skillPathways = (skillstreesResponse?.data as any)?.data as any || [];
      const profileData = (profileResponse?.data as any)?.data as any;
      
      // Get user data from profile endpoint (already includes created_at)
      // No need to call /my-data endpoint - profile endpoint has everything we need
      const userCreatedAt = profileData?.user?.created_at as any;
      let planEndDate: any = null;

      const profileStatistics = profileData?.statistics as any;
      const profileSkillstrees = profileData?.skillstrees as any;
      const profileSubscription = profileData?.subscription as any;
      
      // Calculate enrolled pathways count
      const profilePathways = skillPathways.filter((pathway: any) => pathway.is_enrolled);
      
      // Calculate active (non-completed) enrolled pathways count
      // Only count skillstrees that are not completed as active slots
      const enrolledSkillstrees = profileSkillstrees?.enrolled || [];
      const activeEnrolledPathways = enrolledSkillstrees.filter((st: any) => !st.is_completed).length;
      
      // Use total_enrolled to check if user has ever enrolled (for onboarding modal)
      // But use activeEnrolledPathways for slot calculation
      const enrolledPathways = activeEnrolledPathways;

      const userRole = getUserRole(profileData.user?.role);
      const isPlus = getUserBadge(userRole) === 'PLUS';
      let maxPathwaySlots = 0;
      
      if (userRole.id > 1 && userRole.id < 5) {
        maxPathwaySlots = isPlus ? 3 : 1;
      }

      if (profileSubscription?.expire_date) {
        const endDate = new Date(profileSubscription?.expire_date);
        planEndDate = endDate.toISOString();
      } else if (!planEndDate && !isPlus && userCreatedAt) {
        // Set plan end date to 7 days after account creation if not available
        const endDate = new Date(userCreatedAt);
        endDate.setDate(endDate.getDate() + 7);
        planEndDate = endDate.toISOString();
      }

      // Calculate progress data
      const totalMicroSkills = this.calculateTotalMicroSkills(profilePathways);
      const completedMicroSkills = profileData.statistics.total_micro_skills_completed;
      const overallProgress = totalMicroSkills > 0 
        ? Math.round((completedMicroSkills / totalMicroSkills) * 100)
        : 0;

      // Find current active pathway for display
      const activePathway = profilePathways.find((pathway: any) => 
        pathway.is_enrolled && pathway.versions?.[0]?.learning_progress > 0
      );

      const activePathways = profilePathways.filter((pathway: any) => {
        const firstVersion = pathway.versions?.[0];
        return pathway.is_enrolled && firstVersion && firstVersion.learning_progress > 0;
      });

      // Transform API data to match original component structure
      const transformedPathways: SkillPathway[] = profilePathways.map((pathway: any) => ({
        id: pathway.skillstree_id.toString(),
        skillstree_id: pathway.skillstree_id,
        name: pathway.skillstree_name,
        description: pathway.description,
        category: pathway.core_category_name,
        core_category_id: pathway.core_category_id,
        core_category_name: pathway.core_category_name,
        core_category_slug: pathway.core_category_slug,
        progress: pathway.versions?.[0]?.learning_progress || 0,
        enrolled: pathway.is_enrolled,
        is_enrolled: pathway.is_enrolled,
        learning_progress: pathway.versions?.[0]?.learning_progress || 0,
        versions: pathway.versions || [],
        image_url: pathway.image_url,
        duration: pathway.duration,
        slug: pathway.slug
      }));

      // Group pathways by category
      const groupedPathways = this.groupPathwaysByCategory(transformedPathways);

      return {
        user: {
          id: profileData.user.id,
          name: `${profileData.user.first_name} ${profileData.user.last_name}`,
          email: profileData.user.email,
          first_name: profileData.user.first_name,
          last_name: profileData.user.last_name,
          country: profileData.user.country,
          gender: profileData.user.gender,
          role: userRole,
        },
        skillPathways: transformedPathways,
        groupedPathways, // Add grouped data
        selectedPathway: undefined,
        progress: {          
          total_skillstrees: profileStatistics.total_skillstrees,
          completed_skillstrees: profileStatistics.completed_skillstrees,
          total_micro_skills_started: profileStatistics.total_micro_skills_started,
          total_micro_skills_completed: profileStatistics.total_micro_skills_completed,
          total_badges: profileStatistics.total_badges,
          total_passports: profileStatistics.total_passports,
          total_events_registered: profileStatistics.total_events_registered,
          total_events_attended: profileStatistics.total_events_attended,
          microSkills:profileStatistics.total_skillstrees,
          microLearning: profileStatistics.total_micro_skills_started,
          achievements: profileStatistics.completed_skillstrees,
          overall: overallProgress,
          completedModules: completedMicroSkills,
          totalModules: totalMicroSkills,
          currentPathway: activePathway ? activePathway.skillstree_name : 'Select a pathway to start learning'
        },
        profilePathways,
        enrolledPathways,
        maxPathwaySlots,
        pathwayCount: skillPathways.length,
        statistics: profileData.statistics,
        achievementCount: skillPathways.length - activePathways.length,
        coachingCount: 0,
        networkingCount: 0,
        upcomingSessions: [],
        networkingEvents: [],
        planValidUntil: planEndDate,
        isPlus
      };
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  },

  // Helper method to group pathways by category
  groupPathwaysByCategory(pathways: SkillPathway[]): { [category: string]: SkillPathway[] } {
    const grouped: { [category: string]: SkillPathway[] } = {};
    
    pathways.forEach(pathway => {
      const category = pathway.core_category_name;
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(pathway);
    });
    
    return grouped;
  },

  async getUserProfile() {
    return api.get('/profile');
  },

  async getUserData() {
    return api.get('/my-data');
  },

  async getCategories() {
    return api.get('/v1/core-category');
  },

  async getCategory(slug: string) {
    return api.get(`/v1/core-category/${slug}`);
  },

  async getSkillstrees() {
    return api.get('/v1/skillstrees');
  },

  async getSkillstree(pathwayId: string) {
    const skillstreeId = parseInt(pathwayId);
    return api.get(`/v1/skillstrees/${skillstreeId}`);
  },

  async getMicroskill(skillId: string) {
    const microId = parseInt(skillId);
    return api.get(`/v1/microskills/${microId}`);
  },

  async enrollInPathway(pathwayId: string) {
    const skillstreeId = parseInt(pathwayId);
    return api.post(`/v1/skillstrees/${skillstreeId}/enroll`);
  },

  async launchSkillstree() {
    // Since there's no specific launch endpoint, return success
    return Promise.resolve({ data: { success: true } });
  },

  async updateProgress(pathwayId: string, progress: number) {
    // Implement progress update if API available
    return Promise.resolve({ data: { success: true } });
  },
  
  // Get all events
  async getEvents() {
    return api.get(`/dashboard/events`);
  },

  calculateTotalMicroSkills(skillPathways: any[]): number {
    let total = 0;
    skillPathways.forEach(pathway => {
      pathway.versions?.forEach((version: any) => {
        total += version.micro_skills?.length || 0;
      });
    });
    return total;
  }
};